import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Package, LogOut, Clock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { api } from '../api';
import { formatMoney } from '../utils/money';

export default function AccountPage() {
  const { user, loading, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const money = (v) => formatMoney(v, settings);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.getMyOrders()
      .then((res) => setOrders(res.items || res.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [user]);

  if (loading || !user) {
    return <div style={{ paddingTop: '140px', textAlign: 'center', minHeight: '60vh', color: 'var(--mid)' }}>Loading…</div>;
  }

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '48px 24px 90px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '14px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ width: '54px', height: '54px', borderRadius: '50%', background: 'var(--forest)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={24} color="#fff" />
            </div>
            <div>
              <h1 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1.5rem', color: 'var(--forest)' }}>{user.name || 'My account'}</h1>
              <p style={{ color: 'var(--mid)', fontSize: '0.86rem' }}>{user.email}</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate('/'); }} style={{
            display: 'inline-flex', gap: '7px', alignItems: 'center', background: '#fff',
            border: '1px solid var(--mist)', color: 'var(--charcoal)', padding: '10px 16px',
            borderRadius: '10px', fontSize: '0.83rem', fontWeight: 600,
          }}><LogOut size={15} /> Log out</button>
        </div>

        <h2 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1.15rem', color: 'var(--charcoal)', marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Package size={18} /> Order history
        </h2>

        {ordersLoading ? (
          <p style={{ color: 'var(--mid)' }}>Loading orders…</p>
        ) : orders.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid var(--mist)', borderRadius: '16px', padding: '40px', textAlign: 'center' }}>
            <p style={{ color: 'var(--mid)', marginBottom: '18px' }}>You haven't placed any orders yet.</p>
            <Link to="/shop" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'var(--forest)', color: '#fff', padding: '12px 24px', borderRadius: '10px', fontFamily: 'Syne', fontWeight: 700, fontSize: '0.83rem', textTransform: 'uppercase' }}>Start shopping</Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {orders.map((o) => (
              <Link key={o.id || o._id} to={`/order/${o.id || o._id}`} style={{
                background: '#fff', border: '1px solid var(--mist)', borderRadius: '14px', padding: '18px 20px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
              }}>
                <div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, color: 'var(--charcoal)', fontSize: '0.95rem' }}>#{o.orderNumber || o.id || o._id}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--mid)' }}>
                    {new Date(o.createdAt).toLocaleDateString()} · {(o.items || []).length} item{(o.items || []).length !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                  <StatusPill status={o.status || 'pending'} />
                  <span style={{ fontFamily: 'Syne', fontWeight: 700, color: 'var(--forest)' }}>{money(o.total)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    pending: { bg: '#fff4e0', fg: '#b8860b', Icon: Clock },
    paid: { bg: '#e8f5ec', fg: '#2e7d4f', Icon: CheckCircle2 },
    processing: { bg: '#e7f0fb', fg: '#2b6cb0', Icon: Package },
    shipped: { bg: '#e7f0fb', fg: '#2b6cb0', Icon: Package },
    delivered: { bg: '#e8f5ec', fg: '#2e7d4f', Icon: CheckCircle2 },
    cancelled: { bg: '#fdecec', fg: '#c0392b', Icon: Clock },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', background: s.bg, color: s.fg, fontSize: '0.72rem', fontWeight: 700, padding: '5px 11px', borderRadius: '999px', textTransform: 'capitalize' }}>
      <s.Icon size={12} /> {status}
    </span>
  );
};
